import { FC, useCallback } from 'react';
import { useRouter } from 'shared-routing';

import SectionContainer from '@common/components/SectionContainer';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { openJourneyPickerDates } from '@features/JourneyPickerV2/openJourneyPickerDates';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useJourneyPickerAvailability } from '@features/JourneyPickerV2/useJourneyPickerAvailability';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useRatePageSettings } from '@features/rates';
import { useTranslations } from '@lang';
import { useRatesErrorMessage } from '@common/hooks/useRatesErrorMessage';

import { AccommodationListTemplate } from 'voyager';
import type { JourneyPickerSearchParams } from 'voyager';

import { parseDateToString } from '@utils/helpers';
import { queryParamToString } from '@utils/queryParam';
import { useRatesList } from './useRatesList';
import { RatesV2Props } from './RatesV2.types';

export const RatesV2: FC<RatesV2Props> = ({ journeyParams, language, changeJourneyParam }) => {
  const router = useRouter();
  const { t } = useTranslations();

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const { listItems, isFetching, error } = useRatesList(journeyParams, language);
  const { content: ratesPageContent } = useRatePageSettings(
    language,
    t('ratesListing.sectionTitle')
  );
  const errorContent = useRatesErrorMessage(error);
  const noResultsLabel = error ? errorContent.message : t('ratesListing.noResults');

  const { availability, isLoadingAvailability, onMonthChange } = useJourneyPickerAvailability({
    adults: journeyParams.adults ?? 0,
    children: journeyParams.children ?? 0,
    initialMonth: journeyParams.fromDate?.format('YYYY-MM-DD'),
  });

  const handleSearchComplete = useCallback(
    (params: JourneyPickerSearchParams) => {
      const adults = params.adults;
      const children = params.children ?? 0;
      const promoRateCode = queryParamToString(router.query.promoRateCode);
      const rateCode = queryParamToString(router.query.rateCode);

      changeJourneyParam('rooms', [{ roomNumber: 1, adults, children }]);
      changeJourneyParam('childAges', []);

      router.push({
        pathname: Routes.RATE_LIST,
        query: {
          fromDate: parseDateToString(params.fromDate),
          toDate: params.toDate ? parseDateToString(params.toDate) : undefined,
          adults: String(adults),
          children: String(children),
          ...(promoRateCode ? { promoRateCode } : undefined),
          ...(rateCode ? { rateCode } : undefined),
        },
      });
    },
    [router, changeJourneyParam]
  );

  const handleEditSearch = useCallback(() => {
    openJourneyPickerDates();
  }, []);

  const promoRateCode =
    queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode ?? '';

  const handleRateCodeChange = useCallback(
    (nextCode: string) => {
      const rateCode = queryParamToString(router.query.rateCode);
      const trimmed = nextCode.trim();
      changeJourneyParam('promoRateCode', trimmed || undefined);
      router.push({
        pathname: Routes.RATE_LIST,
        query: {
          ...router.query,
          promoRateCode: trimmed || undefined,
          rateCode: rateCode || undefined,
        },
      });
    },
    [router, changeJourneyParam]
  );

  const handleSelectRate = useCallback(
    (id: string) => {
      const promoRateCode =
        queryParamToString(router.query.promoRateCode) ?? journeyParams.promoRateCode ?? undefined;

      router.push({
        pathname: Routes.RATE_DETAIL,
        query: {
          id,
          fromDate: parseDateToString(journeyParams.fromDate),
          toDate: parseDateToString(journeyParams.toDate),
          adults: String(journeyParams.adults),
          children: String(journeyParams.children),
          ...(promoRateCode ? { promoRateCode } : undefined),
        },
      });
    },
    [
      router,
      journeyParams.fromDate,
      journeyParams.toDate,
      journeyParams.adults,
      journeyParams.children,
      journeyParams.promoRateCode,
    ]
  );

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="range"
            dateContext="stay"
            showGuests
            categories={navCategories}
            category="rates"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
            availability={availability}
            isLoadingAvailability={isLoadingAvailability}
            onMonthChange={onMonthChange}
            showRate
            rateCode={promoRateCode}
            onRateCodeChange={handleRateCodeChange}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SectionContainer>
        <Spinner showDelayMessage isActive={isFetching && listItems.length > 0} />
        <AccommodationListTemplate
          items={listItems}
          onSelectItem={handleSelectRate}
          isLoading={isFetching && listItems.length === 0}
          titleLabel={ratesPageContent.title}
          fromPriceLabel={t('common.from')}
          soldOutLabel={t('common.unavailable')}
          noResultsLabel={noResultsLabel}
          noImageLabel={t('basket.noImageLabel')}
          editSearchLabel={t('common.editSearch')}
          onEditSearch={handleEditSearch}
          columns={2}
        />
      </SectionContainer>
    </EntitiesListingWrapper>
  );
};
